import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface DebugLog {
  timestamp: string;
  message: string;
  data?: any;
}

interface TestResult {
  success: boolean;
  data?: any;
  error?: string;
}

export default function DebugMobilePage() {
  const [debugInfo, setDebugInfo] = useState<DebugLog[]>([]);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  const addDebugLog = (message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    setDebugInfo(prev => [...prev, { timestamp, message, data }]);
  };

  const testCitiesAPI = async () => {
    addDebugLog('Testing Cities API...');
    
    try {
      // Test different URL formats
      const urls = [
        '/api/simulation/cities',
        '/.netlify/functions/api/simulation/cities',
        window.location.origin + '/api/simulation/cities',
        window.location.origin + '/.netlify/functions/api/simulation/cities'
      ];

      // Add Vercel-specific URL if detected
      if (window.location.hostname.includes('vercel.app')) {
        addDebugLog('Vercel deployment detected - testing Vercel API endpoints');
      }

      for (const url of urls) {
        try {
          addDebugLog(`Trying URL: ${url}`);
          const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          addDebugLog(`Response status: ${response.status} for ${url}`);
          
          if (response.ok) {
            const data = await response.json();
            addDebugLog(`Success! Got ${data.length} cities from ${url}`, data);
            setTestResults(prev => ({ ...prev, [url]: { success: true, data } }));
          } else {
            const error = await response.text();
            addDebugLog(`Error from ${url}: ${error}`);
            setTestResults(prev => ({ ...prev, [url]: { success: false, error } }));
          }
        } catch (err: any) {
          const errorMessage = err?.message || String(err);
          addDebugLog(`Fetch failed for ${url}: ${errorMessage}`);
          setTestResults(prev => ({ ...prev, [url]: { success: false, error: errorMessage } }));
        }
      }
    } catch (err: any) {
      addDebugLog('Test failed', err);
    }
  };

  const getEnvironmentInfo = () => {
    const info = {
      hostname: window.location.hostname,
      origin: window.location.origin,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      isNetlify: window.location.hostname.includes('netlify.app'),
      netlifyApiUrl: (window as any).NETLIFY_API_URL,
      viteApiUrl: import.meta.env.VITE_API_URL,
      mode: import.meta.env.MODE,
      buildTime: (window as any).BUILD_TIME
    };
    
    addDebugLog('Environment Info', info);
    return info;
  };

  useEffect(() => {
    addDebugLog('Debug page loaded');
    getEnvironmentInfo();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Mobile Debug Tool</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={() => setDebugInfo([])}>Clear Logs</Button>
            <Button onClick={getEnvironmentInfo}>Check Environment</Button>
            <Button onClick={testCitiesAPI}>Test Cities API</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm font-mono">
              {Object.entries(testResults).map(([url, result]: [string, any]) => (
                <div key={url} className={`p-2 rounded ${result.success ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div className="font-bold">{url}</div>
                  <div>{result.success ? `✓ Success (${result.data?.length} cities)` : `✗ Failed: ${result.error}`}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Debug Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {debugInfo.map((log, index) => (
                <div key={index} className="text-xs border-b pb-2">
                  <div className="text-gray-500">{log.timestamp}</div>
                  <div className="font-medium">{log.message}</div>
                  {log.data && (
                    <pre className="text-xs bg-gray-100 p-2 mt-1 rounded overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}